/**
 * Die Aufzaehlungen des Core.
 *
 * Diese Listen sind der teuerste Teil des Vertrags: Ein zusaetzlicher Wert ist
 * billig, ein entfernter oder umbenannter Wert bricht jede gespeicherte
 * Buchung, die ihn benutzt. Buchungen werden nie geloescht (CLAUDE.md §3.2) --
 * ein Wert, der einmal im Log steht, muss also fuer immer lesbar bleiben.
 */
import { z } from "zod";
/** KONZEPT.md §9.1 -- was fuer ein Ding der Artikel ist. */
export const ArtikelArt = z.enum([
    /** Einzelnes Geraet. Der Normalfall. */
    "geraet",
    /** Behaelter, der selbst Artikel *und* Lagerort ist (KONZEPT.md §5.2). */
    "packstueck",
    /** Kiste mit Stueckliste; der Inhalt lebt im Modul Kleinteile (§12). */
    "kleinteilekiste",
    /** Wird mengenmaessig gefuehrt, nicht stueckweise (§12.6). */
    "verbrauchsmaterial",
]);
/**
 * Arten, die stueckweise gefuehrt werden: fuer sie gewinnt bei der
 * Zustandsableitung die neueste Buchung (KONZEPT.md §7.1).
 */
export const STUECKGUT_ARTEN = [
    "geraet",
    "packstueck",
    "kleinteilekiste",
];
export function istStueckgut(art) {
    return STUECKGUT_ARTEN.includes(art);
}
/**
 * **Wo** ein Artikel ist -- und bei wem (ADR 0005).
 *
 * Fest vorgegeben und vom System gesetzt, nie von Hand: Der Verbleib folgt
 * allein aus der Art der Buchung. Einbuchen und Umlagern fuehren ins Lager,
 * die Entnahme in den Einsatz, die Ausgabe zur Ausleihe. Module wie Ausleihe
 * und Einsatzstellen verlassen sich auf genau diese Werte; deshalb sind sie
 * nicht einstellbar.
 *
 * Unabhaengig davon steht der **Status** (siehe unten): in welchem Zustand der
 * Artikel ist. Ein defektes Geraet bleibt defekt, wenn es in die Werkstatt
 * umgelagert wird (KONZEPT.md §7.4).
 */
export const Verbleib = z.enum([
    /**
     * Der Artikel ist bekannt, aber noch nie gebucht worden -- wir wissen nicht,
     * wo er liegt.
     *
     * Ergaenzung gegenueber KONZEPT.md §7.2. Sie ist noetig, weil der Import der
     * Ausstattungsliste tausend Artikel anlegt, bevor irgendjemand den ersten
     * Code gescannt hat. Diese Artikel als `im_lager` zu fuehren waere eine
     * Behauptung, fuer die es keine Buchung gibt -- und genau davon lebt dieses
     * System nicht. Waehrend der Erfassung ist die Zahl dieser Artikel ausserdem
     * die ehrlichste Fortschrittsanzeige, die es gibt.
     */
    "nicht_erfasst",
    /** liegt an einem realen Lagerort */
    "im_lager",
    /** an einer Einsatzstelle, weiterhin unser Material */
    "im_einsatz",
    /** an Person oder Einheit uebergeben, Empfaenger namentlich bekannt */
    "ausgeliehen",
]);
/**
 * Der **Status** eines Artikels: der Schluessel eines Eintrags aus den
 * Stammdaten (`StatusArt`), etwa `einsatzbereit` oder `defekt`.
 *
 * Welche Status es gibt, wie sie heissen und welche Farbe sie tragen, legt der
 * Ortsverband selbst fest (ADR 0005). Der Schluessel aendert sich dabei nie --
 * er steht in jeder Buchung, die ihn gesetzt hat, und muss fuer immer lesbar
 * bleiben. Deshalb werden Status auch nie geloescht, nur stillgelegt.
 *
 * Die Namen des Verbleibs sind als Schluessel gesperrt. Bis Vertrag 2.0 lagen
 * beide in einer Liste, und eine alte Korrektur kann noch `im_lager` tragen --
 * die Ableitung muss so etwas erkennen koennen.
 */
export const Status = z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,39}$/, "Kleinbuchstaben, Ziffern und Unterstrich, mit einem Buchstaben vorn")
    .refine((s) => !Verbleib.options.includes(s), {
    message: "Dieser Name ist fuer den Verbleib reserviert",
});
/** KONZEPT.md §9.2 -- was eine Buchung bewirkt. */
export const BuchungsArt = z.enum([
    /** auf einen realen Lagerort gebucht -- gilt damit als zurueck */
    "einbuchen",
    /** vom Lagerort entfernt, ohne benanntes Ziel */
    "entnehmen",
    /** an eine Einsatzstelle gebracht */
    "einsatz",
    /** an Person oder Einheit uebergeben (Modul Ausleihe) */
    "ausgabe",
    /** von einer Ausleihe zurueckgenommen */
    "ruecknahme",
    /** Lagerortwechsel innerhalb des Hauses */
    "umlagerung",
    /** setzt den Status (etwa defekt, in Pruefung, verloren) -- nie den Ort */
    "statusaenderung",
    /** Gegenbuchung zu einer fehlerhaften Buchung, mit Begruendung */
    "korrektur",
]);
/** KONZEPT.md §5.1 -- Ebene im Lagerortbaum. */
export const LagerortEbene = z.enum(["ort", "packstueck", "unterebene"]);
/** KONZEPT.md §4 -- zwei Rollen, Feinrechte kommen aus den Modul-Manifesten. */
export const Rolle = z.enum(["benutzer", "administrator"]);
/** KONZEPT.md §6 */
export const EinsatzstellenStatus = z.enum(["aktiv", "abgeschlossen"]);
/**
 * Woher ein Artikel stammt.
 *
 * Wichtig fuer den wiederholten Import der Pruefungsverwaltung: Was vor Ort
 * nacherfasst wurde, darf ein spaeterer Import nicht ueberschreiben
 * (docs/BEFUND-VOR-STUFE-0.md §3).
 */
export const Herkunft = z.enum([
    /** aus einer importierten Ausstattungs- oder Pruefliste */
    "import",
    /** in der Oberflaeche angelegt */
    "manuell",
]);
//# sourceMappingURL=enums.js.map